import {
  labelChanged,
  labelPayload,
  maySetDefault,
  removeExplanation,
  removeLabel,
  type SavedAddress,
} from '@agos/customer-core'
import { T, tt } from '@agos/shared'
import { Check, MapPin, Pencil, Star, X } from 'lucide-react'

/**
 * One saved address, as legacy draws it — §9.248, FB-408, the owner's N3.
 *
 * *"Uložené adresy looks shitty."* Every row rendered its **label editor open**, so four saved
 * addresses read as four text fields stacked under a heading rather than as four addresses.
 *
 * Legacy's row (`MojProfil.tsx:1700`) is a **card**: bordered and tinted when it is the default, a
 * pin coloured to match, the label as an emerald badge, *Predvolená* as its own badge, the address as
 * text — and the editor **behind a pencil**, opened for one address at a time.
 *
 * Its own file, as `OrderHistoryCard` is: `ProfilePage.tsx` has been restructured in place once too
 * often this week.
 */
export function SavedAddressRow({
  entry,
  editing,
  draft,
  saving,
  onEdit,
  onDraft,
  onSave,
  onRemove,
  onSetDefault,
  settingDefault,
}: {
  entry: SavedAddress
  editing: boolean
  draft: string
  saving: boolean
  onEdit: (open: boolean) => void
  onDraft: (value: string) => void
  onSave: (payload: ReturnType<typeof labelPayload>) => void
  onRemove: () => void
  /** §9.332 - the badge was drawn and nothing could set it. */
  onSetDefault: () => void
  settingDefault: boolean
}) {
  return (
    <div
      className={`rounded-xl border transition-colors ${
        entry.isDefault ? 'border-primary/40 bg-primary/5' : 'border-border'
      }`}
      data-address={entry.address}
      data-address-is-default={entry.isDefault ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2 pl-4 pr-2 py-2.5">
        <MapPin
          className={`w-4 h-4 shrink-0 ${entry.isDefault ? 'text-primary' : 'text-primary/70'}`}
        />
        <div className="flex-1 min-w-0">
          {/* The label and *Predvolená* as badges above the address, which is where legacy puts
              them - a suffix on the text is invisible in a list. */}
          {(entry.label !== null || entry.isDefault) && (
            <div className="flex items-center gap-1.5 mb-0.5">
              {entry.label !== null && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded"
                  data-address-label-badge
                >
                  {entry.label}
                </span>
              )}
              {entry.isDefault && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded"
                  data-address-default
                >
                  <T>Predvolená</T>
                </span>
              )}
            </div>
          )}
          <span className="block text-sm text-foreground break-words">{entry.address}</span>
        </div>
        {/* §9.332 - the owner: *"I cant set address to Predvolená."* The badge above has always
            rendered when `isDefault` was true and nothing in the app could make it true. Beside the
            pencil rather than inside the editor: it is a one-click action, not a field to fill. */}
        {maySetDefault(entry) && (
          <button
            type="button"
            data-address-set-default={entry.address}
            aria-label={tt('Nastaviť ako predvolenú')}
            title={tt('Nastaviť ako predvolenú')}
            disabled={settingDefault}
            onClick={onSetDefault}
            className="p-2 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary shrink-0 disabled:opacity-40"
          >
            <Star className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          data-address-edit={entry.address}
          aria-label={tt('Upraviť názov adresy')}
          aria-expanded={editing}
          onClick={() => onEdit(!editing)}
          className="p-2 rounded-lg hover:bg-muted shrink-0"
        >
          {editing ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
        </button>
      </div>

      {editing && (
        <div className="flex gap-2 px-4 pb-3" data-address-editor>
          <input
            data-address-label
            value={draft}
            onChange={(event) => onDraft(event.target.value)}
            placeholder={tt('Názov, napríklad Domov')}
            className="flex-1 min-h-[36px] rounded-xl border border-border bg-card px-3 text-xs"
          />
          <button
            type="button"
            data-address-label-save
            aria-label={tt('Uložiť názov')}
            disabled={saving || !labelChanged(entry, draft)}
            onClick={() => onSave(labelPayload(entry, draft))}
            className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            data-address-remove
            title={removeExplanation(entry)}
            onClick={onRemove}
            className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
          >
            {removeLabel(entry)}
          </button>
        </div>
      )}
    </div>
  )
}
